import type {
  DepartamentoFlat,
  DepartamentoHierarquico,
  LocalizacaoHierarquica,
  TipoVaga,
  Vaga,
} from "../types/vaga";

const GREENHOUSE_BOARDS = [
  {
    quadro: 1 as const,
    origem: "agibank",
    tipo: "abertas" as TipoVaga,
    tipoLabel: "Vaga aberta",
    url: "https://boards-api.greenhouse.io/v1/boards/agibank/jobs?content=true",
  },
  {
    quadro: 2 as const,
    origem: "agibankvagasfuturas",
    tipo: "futuras" as TipoVaga,
    tipoLabel: "Vaga futura",
    url: "https://boards-api.greenhouse.io/v1/boards/agibankvagasfuturas/jobs?content=true",
  },
] as const;

type GreenhouseBoard = (typeof GREENHOUSE_BOARDS)[number];

interface GreenhouseJob {
  id: number;
  title: string;
  updated_at?: string;
  location?: { name?: string } | null;
  departments?: { id: number; name: string; parent_id: number | null }[] | null;
  absolute_url?: string | null;
  content?: string | null;
}

interface GreenhouseResponse {
  jobs: GreenhouseJob[];
}

export interface VagasServiceResponse {
  vagas: Vaga[];
  vagasAbertas: Vaga[];
  vagasFuturas: Vaga[];
  departamentos: DepartamentoHierarquico[];
  departamentosFlat: DepartamentoFlat[];
  localizacoes: LocalizacaoHierarquica[];
}

function parseLocationForFilter(
  name?: string | null,
): { cidade?: string; estado: string } | null {
  if (!name) return null;
  if (name.includes(";")) return null;

  const parts = name
    .split(", ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 3 && parts[2] === "Brasil") {
    return { cidade: parts[0], estado: parts[1] };
  }
  if (parts.length === 2 && parts[1] === "Brasil") {
    return { estado: parts[0] };
  }
  return null;
}

function parseLocationDisplay(name?: string | null): {
  cidade: string;
  estado: string;
} {
  const fallback = { cidade: "Remoto", estado: "Remoto" };
  const parsed = parseLocationForFilter(name);
  if (parsed) {
    return {
      cidade: parsed.cidade ?? parsed.estado,
      estado: parsed.estado,
    };
  }

  if (!name) return fallback;
  const parts = name
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return { cidade: parts[0], estado: parts[1] };
  }
  const dashParts = name
    .split(" - ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (dashParts.length >= 2) {
    return { cidade: dashParts[0], estado: dashParts[1] };
  }
  return { cidade: parts[0] || name, estado: "Remoto" };
}

function buildDepartamentos(jobs: GreenhouseJob[]): {
  tree: DepartamentoHierarquico[];
  flat: DepartamentoFlat[];
} {
  const depsMap = new Map<string, DepartamentoFlat>();

  jobs.forEach((job) => {
    const deps = job.departments ?? [];
    if (deps.length === 0) {
      return;
    }

    deps.forEach((dep) => {
      const id = String(dep.id);
      if (!depsMap.has(id)) {
        depsMap.set(id, {
          id,
          nome: dep.name,
          parentId: dep.parent_id ? String(dep.parent_id) : null,
        });
      }

      if (dep.parent_id) {
        const parentId = String(dep.parent_id);
        if (!depsMap.has(parentId)) {
          depsMap.set(parentId, {
            id: parentId,
            nome: "(Departamento Pai)",
            parentId: null,
          });
        }
      }
    });
  });

  const flat = Array.from(depsMap.values());

  const depNameLookup = new Map<string, string>();
  jobs
    .flatMap((job) => job.departments ?? [])
    .forEach((dep) => depNameLookup.set(String(dep.id), dep.name));

  flat.forEach((dep) => {
    if (dep.nome !== "(Departamento Pai)") return;
    const resolved = depNameLookup.get(dep.id);
    if (resolved) {
      dep.nome = resolved;
    }
  });

  const depIdsComVagas = new Set<string>(
    jobs.flatMap((job) => job.departments ?? []).map((dep) => String(dep.id)),
  );

  const paisSemVaga = flat.filter(
    (dep) =>
      !depIdsComVagas.has(dep.id) &&
      flat.some((child) => child.parentId === dep.id),
  );

  paisSemVaga.forEach((pai) => {
    flat.forEach((dep) => {
      if (dep.parentId === pai.id) {
        dep.parentId = null;
      }
    });
  });

  const flatSemPais = flat.filter(
    (dep) => !paisSemVaga.some((pai) => pai.id === dep.id),
  );

  const buildTree = (
    deps: DepartamentoFlat[],
    parentId: string | null = null,
  ): DepartamentoHierarquico[] => {
    return deps
      .filter((dep) => dep.parentId === parentId)
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map((dep) => {
        const children = buildTree(deps, dep.id);
        return {
          id: dep.id,
          nome: dep.nome,
          ...(children.length > 0 ? { subdepartamentos: children } : {}),
        };
      });
  };

  return { tree: buildTree(flatSemPais), flat: flatSemPais };
}

function buildLocalizacoes(jobs: GreenhouseJob[]): LocalizacaoHierarquica[] {
  const map = new Map<string, Set<string>>();

  jobs.forEach((job) => {
    const locationName = job.location?.name;
    const parsed = parseLocationForFilter(locationName);
    if (!parsed) return;
    if (!map.has(parsed.estado)) {
      map.set(parsed.estado, new Set());
    }
    if (parsed.cidade) {
      map.get(parsed.estado)?.add(parsed.cidade);
    }
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([estado, cidades]) => ({
      estado,
      cidades: Array.from(cidades).sort((a, b) => a.localeCompare(b)),
    }));
}

function mapJobToVaga(job: GreenhouseJob, board: GreenhouseBoard): Vaga {
  const departamentos = job.departments?.map((dep) => dep.name) ?? [];
  const departamentosIds = job.departments?.map((dep) => String(dep.id)) ?? [];
  const departamentoLabel =
    departamentos.length > 0 ? departamentos.join(" / ") : "Outros";
  const { cidade, estado } = parseLocationDisplay(job.location?.name);

  return {
    id: `${board.origem}-${job.id}`,
    idOriginal: String(job.id),
    titulo: job.title,
    departamento: departamentoLabel,
    departamentoIds: departamentosIds,
    tipo: board.tipo,
    tipoLabel: board.tipoLabel,
    quadro: board.quadro,
    origem: board.origem,
    localizacao: { cidade, estado },
    locationName: job.location?.name,
    dataAtualizacao: job.updated_at ? new Date(job.updated_at) : new Date(),
    urlAplicacao: job.absolute_url || "",
    conteudo: job.content ? stripHtml(job.content) : undefined,
    requisitos: job.content
      ? extractRequisitosFromHtml(job.content)
      : undefined,
  };
}

/**
 * Decodifica entidades HTML (e.g. &lt; → <, &amp; → &), sem remover tags.
 */
function decodeHtmlEntities(html: string): string {
  return html
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

/**
 * Remove tags HTML e decodifica entidades, retornando texto puro.
 */
function stripHtml(html: string): string {
  return decodeHtmlEntities(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extrai apenas as seções de requisitos/diferenciais/desafios do HTML da vaga.
 * Captura o texto entre cabeçalhos relevantes e para ao encontrar seções irrelevantes.
 */
function extractRequisitosFromHtml(html: string): string {
  // Decodifica entidades HTML para que as tags fiquem como tags reais
  const htmlDecodificado = decodeHtmlEntities(html);

  const secaoInicio = [/requisitos/i, /diferenciais/i];

  const secaoFim = [
    /benef[ií]cios/i,
    /benef[ií]cios e facilidade/i,
    /facilidades/i,
    /sobre n[oó]s/i,
    /modelo de trabalho/i,
    /programa t[oô] de dono/i,
    /empresa contratante/i,
    /saiba mais/i,
    /confira nossas/i,
  ];

  // Split por qualquer tag de bloco relevante (h1-h6, p, li, ul, strong, div)
  const blocos = htmlDecodificado.split(
    /(?=<(?:h[1-6]|p|li|ul|ol|strong|div)[^>]*>)/i,
  );

  let capturando = false;
  const resultado: string[] = [];

  for (const bloco of blocos) {
    const textoBloco = stripHtml(bloco);
    const textoBlocoLower = textoBloco.toLowerCase();

    // Ignora blocos vazios
    if (!textoBloco.trim()) continue;

    // Verifica se esse bloco inicia uma seção irrelevante (para de capturar)
    // Checado ANTES de secaoInicio para evitar que palavras como "desafios"
    // dentro de seções irrelevantes (ex: "Programa Tô de Dono") reativem a captura.
    if (secaoFim.some((regex) => regex.test(textoBlocoLower))) {
      capturando = false;
      continue;
    }

    // Verifica se esse bloco inicia uma seção relevante.
    // O bloco precisa ser curto (< 150 chars) para garantir que é um cabeçalho
    // e não um parágrafo longo que contém a palavra incidentalmente.
    if (
      textoBloco.length < 150 &&
      secaoInicio.some((regex) => regex.test(textoBlocoLower))
    ) {
      capturando = true;
      resultado.push(textoBloco);
      continue;
    }

    // Se estamos capturando, adiciona o bloco
    if (capturando) {
      resultado.push(textoBloco);
    }
  }

  return resultado.join(" ").replace(/\s+/g, " ").trim();
}

async function fetchFromBoard(board: GreenhouseBoard): Promise<GreenhouseJob[]> {
  try {
    const response = await fetch(board.url, { cache: "no-store" });

    if (!response.ok) {
      console.error(
        `Falha ao buscar vagas de ${board.url}: ${response.status} ${response.statusText}`,
      );
      return [];
    }

    const data = (await response.json()) as GreenhouseResponse;
    return data.jobs ?? [];
  } catch (error) {
    console.error(`Erro ao buscar vagas de ${board.url}:`, error);
    return [];
  }
}

export async function fetchVagas(): Promise<VagasServiceResponse> {
  const resultados = await Promise.all(
    GREENHOUSE_BOARDS.map(async (board) => ({
      board,
      jobs: await fetchFromBoard(board),
    })),
  );

  // Mantém a separação por quadro e evita colisão de IDs entre boards.
  const jobsMap = new Map<string, { board: GreenhouseBoard; job: GreenhouseJob }>();
  resultados.forEach(({ board, jobs }) => {
    jobs.forEach((job) => {
      const uniqueJobId = `${board.origem}-${job.id}`;
      if (!jobsMap.has(uniqueJobId)) {
        jobsMap.set(uniqueJobId, { board, job });
      }
    });
  });

  const allEntries = Array.from(jobsMap.values());
  const allJobs = allEntries.map(({ job }) => job);

  if (allJobs.length === 0) {
    throw new Error(
      "Falha ao buscar vagas: nenhum quadro retornou resultados.",
    );
  }

  const vagasAbertas = allEntries
    .filter(({ board }) => board.tipo === "abertas")
    .map(({ board, job }) => mapJobToVaga(job, board));

  const vagasFuturas = allEntries
    .filter(({ board }) => board.tipo === "futuras")
    .map(({ board, job }) => mapJobToVaga(job, board));

  const vagas = [...vagasAbertas, ...vagasFuturas];

  const departamentos = buildDepartamentos(allJobs);

  return {
    vagas,
    vagasAbertas,
    vagasFuturas,
    departamentos: departamentos.tree,
    departamentosFlat: departamentos.flat,
    localizacoes: buildLocalizacoes(allJobs),
  };
}
